import Vue from "vue";
import {AXIOS} from "@/lib/http-common";
import Component from "vue-class-component";
import Item from "@/components/items/model/Item";
import {UserRoleWithPrefix} from "@/components/users/model/UserRoleWithPrefix";
import ItemMeasurement from "@/components/items/model/ItemMeasurement";
import UserField from "@/components/configuration/model/UserField";
import PropertiesService from "@/components/properties/PropertiesService";
import Property from "@/components/properties/model/Property";
import StatusIndicator from "@/views/status/StatusIndicator.vue";
import {Cache} from "three";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import {MeasureUnitKey} from "@/components/properties/settings/measure-units/model/MeasureUnitKey";
import ItemRequest from "@/components/items/model/ItemRequest";
import {DimensionUnit} from "@/components/properties/settings/measure-units/model/DimensionUnit";
import {WeightUnit} from "@/components/properties/settings/measure-units/model/WeightUnit";
import {VolumeUnit} from "@/components/properties/settings/measure-units/model/VolumeUnit";
import {ItemsHelper} from "@/views/items/ItemsHelper";
import {EVENTBUS_TOPICS, STOMP_TOPICS, USER_FIELDS} from "@/lib/constants";
import {FieldDataType} from "@/components/configuration/model/FieldDataType";
import {EventBus} from "@/lib/event-bus";
import {Message, Subscription} from "webstomp-client";
import {CameraKey} from "@/components/properties/settings/camera/CameraKey";
import {ScaleRequestMode} from "@/components/properties/settings/scale/ScaleRequestMode";
import {ScaleProperties} from "@/components/properties/settings/scale/ScaleProperties";
import GateMeasureData from "@/components/device/GateMeasureDate";
import {i18n} from "@/main";
import {ItemHighlightingMode} from "@/components/properties/settings/dimensions/model/ItemHighlightingMode";
import {DimensioningModeKey} from "@/components/properties/settings/dimensions/model/DimensioningModeKey";

@Component({
  components: {StatusIndicator},
  computed: {
    FieldDataType() {
      return FieldDataType;
    },
    Cache() {
      return Cache;
    },
    UserRoleWithPrefix() {
      return UserRoleWithPrefix;
    },
  },
})
export default class Items extends Vue {

  get formattedWeight(): number | null {
    return ItemsHelper.getFormattedMeasurement(this.scannedItem.itemMeasurement.weight);
  }

  set formattedWeight(value: number | null) {
    this.scannedItem.itemMeasurement.weight = ItemsHelper.setMeasurementValue(value);
  }

  get formattedLength(): number | null {
    return ItemsHelper.getFormattedMeasurement(this.scannedItem.itemMeasurement.length);
  }

  set formattedLength(value: number | null) {
    this.scannedItem.itemMeasurement.length = ItemsHelper.setMeasurementValue(value);
  }

  get formattedWidth(): number | null {
    return ItemsHelper.getFormattedMeasurement(this.scannedItem.itemMeasurement.width);
  }

  set formattedWidth(value: number | null) {
    this.scannedItem.itemMeasurement.width = ItemsHelper.setMeasurementValue(value);
  }

  get formattedHeight(): number | null {
    return ItemsHelper.getFormattedMeasurement(this.scannedItem.itemMeasurement.height);
  }

  set formattedHeight(value: number | null) {
    this.scannedItem.itemMeasurement.height = ItemsHelper.setMeasurementValue(value);
  }

  get computedItems() {
    return this.items.map(item => ({
      ...item,
      uniqueKey: `${item.itemNumber}-${item.secondaryField}`,
      ...this.userFields.reduce((acc: Record<string, string>, userField) => {
        acc[`${USER_FIELDS.USER_FIELD_ID_PREFIX}${userField.fieldId}`] = this.getFieldValue(item, userField.fieldId);
        return acc;
      }, {})
    }));
  }

  get itemsTH() {
    const headers = [
      {text: this.$t("items.tableHeaders.itemNumber"), value: "itemNumber"},
      {text: this.$t("items.tableHeaders.secondaryField"), value: "secondaryField"},
      {
        text: `${this.$t("items.tableHeaders.itemMeasurement.length")}(${this.dimensionUnit})`,
        value: "itemMeasurement.length",
      },
      {
        text: `${this.$t("items.tableHeaders.itemMeasurement.width")}(${this.dimensionUnit})`,
        value: "itemMeasurement.width",
      },
      {
        text: `${this.$t("items.tableHeaders.itemMeasurement.height")}(${this.dimensionUnit})`,
        value: "itemMeasurement.height",
      },
      {
        text: `${this.$t("items.tableHeaders.itemMeasurement.weight")}(${this.weightUnit})`,
        value: "itemMeasurement.weight",
      },
      {
        text: `${this.$t("items.tableHeaders.itemMeasurement.volume")}(${this.volumeUnit})`,
        value: "itemMeasurement.volume",
      },
      {text: this.$t("items.tableHeaders.genDate"), value: "gendate"},
      {text: this.$t("items.tableHeaders.modDate"), value: "moddate"},
    ];
    this.userFields.forEach((userField) => {
      const hasFieldValue = this.hasFieldValueInAnyItem(this.items, userField.fieldId);
      if (hasFieldValue) {
        headers.push({
          text: userField.fieldName.toString(),
          value: `${USER_FIELDS.USER_FIELD_ID_PREFIX}${userField.fieldId}`,
        });
      }
    });

    return headers;
  }
  get secondaryFieldBoolean(): boolean {
    const value = this.scannedItem.secondaryField;
    return value === 'true';
  }

  set secondaryFieldBoolean(newValue: boolean) {
    this.scannedItem.secondaryField = newValue.toString(); // convert boolean to string
  }

  public isRequired = [(value: boolean) => {
    if (this.secondaryField && this.secondaryField.fieldDataType === 'BOOLEAN') {
      return false;
    }
    return value ? true : i18n.tc('global.rules.required');
  }];

  public items: Item[] = [];
  public userFields: UserField[] = [];
  public enabledUserFields: UserField[] = [];
  public userFieldValues: { [key: string]: string } = {};
  public scannedItem: Item = this.initNewItem();
  public itemImage = null;
  public itemScrapeScreen = null;
  public imageFilename = '';
  public secondaryFieldEnabled: boolean = false;
  public secondaryField: UserField | undefined;
  public loading: boolean = true;
  public isGateMeasureLoading: boolean = false;
  public properties: Property[] = [];
  public dimensionUnit: DimensionUnit = DimensionUnit.MILLIMETERS;
  public weightUnit: WeightUnit = WeightUnit.KILOGRAMS;
  public volumeUnit: VolumeUnit = VolumeUnit.CUBIC_MM;
  public itemsDialog: boolean = false;
  public deleteItemDialogOpened: boolean = false;
  public tab: number = 0;

  public search: string = '';
  public selectedItems: Item[] = [];
  public sortBy: string = 'moddate';
  public sortDesc: boolean = true;
  public textColorCss: string = 'text-input-white';
  public existingItem: boolean = false;
  public itemMeasurementsSub!: Subscription;
  public itemWeighSub!: Subscription;
  public itemWeighResultsSub!: Subscription;
  public itemMeasurementsErrorsSub!: Subscription;
  public itemScrapeScreenSub!: Subscription;
  public itemScrapeScreenLoading: boolean = false;
  public cameraConnected: boolean = false;
  public cameraEnabled: boolean = false;
  public itemHighlightingEnabled: ItemHighlightingMode = ItemHighlightingMode.DISABLED;
  public cameraIpAddress: string = '';
  public cameraIpPort: string = '80';
  public captureAfterMeasurementProperty: boolean = false;
  public captureAfterBarcodeScanProperty: boolean = false;
  public image2dEnabled: boolean = false;
  public showCameraLiveModal: boolean = false;
  public mjpegStreamUrl: string = '';
  private propertyService: PropertiesService = PropertiesService.getInstance();
  private player: any = null;
  private retryCount: number = 0;
  private maxRetries: number = 3;
  private retryDelayMs: number = 2000;
  private streamError: boolean = false;

  public hasFieldValueInAnyItem(items: Item[], fieldId: string): boolean {
    return items.some((item) => {
      const userFields: UserField[] = item.userFields;
      if (userFields && userFields.length > 0) {
        const userField = item.userFields.find(field => field.fieldId === fieldId);
        return userField && userField.fieldId !== USER_FIELDS.SECONDARY_FIELD && userField.currentValue !== "";
      }
      return false;
    });
  }

  public mapItemWithFormattedMeasurements(item: Item): Item {
    return {
      ...item,
      'itemMeasurement.length': item.itemMeasurement.length !== null
        ? item.itemMeasurement.length.toFixed(4) : '',
      'itemMeasurement.width': item.itemMeasurement.width !== null
        ? item.itemMeasurement.width.toFixed(4) : '',
      'itemMeasurement.height': item.itemMeasurement.height !== null
        ? item.itemMeasurement.height.toFixed(4) : '',
      'itemMeasurement.weight': item.itemMeasurement.weight !== null
        ? item.itemMeasurement.weight.toFixed(4) : '',
      'itemMeasurement.volume': item.itemMeasurement.volume !== null
        ? item.itemMeasurement.volume.toFixed(4) : '',
    };
  }

  public async mounted() {
    this.loading = true;
    await this.loadSettings();
    await this.getItemsFromBackend();
    EventBus.$on(EVENTBUS_TOPICS.WEBSOCKET_CONNECTED, this.websocketConnected);
    this.websocketConnected(this.$store.state.stompClient.connected);

    this.focusItemNumber();
    window.addEventListener("keydown", this.handleGlobalKeydown);
  }

  public async activated() {
    await this.loadSettings();
  }

  public async loadSettings() {
    this.loading = true;
    await Promise.all([
      this.getDimensionUnitFromBackend(),
      this.getWeightUnitFromBackend(),
      this.getVolumeUnitFromBackend(),
      this.getUserFieldsFromBackend(),
      this.getCameraSettingsFromDatabase(),
      this.getItemHighlightingFromBackend(),
    ]);

    this.loading = false;
  }

  public websocketConnected(state: boolean) {
    if (state) {
      this.itemMeasurementsSub = this.$store.state.stompClient.subscribe(STOMP_TOPICS.ITEM_MEASURE,
        async (message: Message) => {
          if (!(this.scannedItem.itemNumber && this.scannedItem.itemNumber !== '')) {
            return;
          }

          const isScaleEnabled = await this.getScaleProperty("scaleEnabled");
          const scaleRequestMode = await this.getScaleProperty("scaleRequestMode");
          const shouldWeighAfter = await this.getScaleProperty(ScaleProperties.WEIGH_AFTER);

          const gateMeasureData: GateMeasureData = JSON.parse(message.body);
          this.scannedItem.itemMeasurement.length = gateMeasureData.length;
          this.scannedItem.itemMeasurement.height = gateMeasureData.height;
          this.scannedItem.itemMeasurement.width = gateMeasureData.width;

          if (isScaleEnabled !== "true") {
            this.scannedItem.itemMeasurement.weight = gateMeasureData.weight;
          }
          this.scannedItem.itemMeasurement = ItemsHelper
            .convertMeasurementsToConfiguredUnits(this.scannedItem.itemMeasurement,
              this.dimensionUnit, this.weightUnit, this.volumeUnit);
          this.updateVolume();

          if (this.captureAfterMeasurementProperty && this.cameraEnabled) {
            await this.getCapturedImage();
          }
          if (this.image2dEnabled) {
            this.get2dImage();
          }

          UserActionResultSender.showSuccessFeedback('devices.commands.success.gateMeasureScanned');

          if (isScaleEnabled === "true" && scaleRequestMode === ScaleRequestMode.REQUEST && shouldWeighAfter === "true") {
            await AXIOS.post('/device/scale-weigh')
              .catch((error) => {
                UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
              });
          }

          this.isGateMeasureLoading = false;
        }
      );

      this.itemMeasurementsErrorsSub = this.$store.state.stompClient.subscribe(
        STOMP_TOPICS.ITEM_MEASURE_ERRORS,
        (message: Message) => {
          const errorData = JSON.parse(message.body);
          UserActionResultSender.showErrorFeedback(errorData.responseCode);
          this.isGateMeasureLoading = false;
        }
      );

      this.itemWeighSub = this.$store.state.stompClient.subscribe(STOMP_TOPICS.ITEM_WEIGH, (message: Message) => {
          if (!(this.scannedItem.itemNumber && this.scannedItem.itemNumber !== '')) {
            return;
          }
          this.scannedItem.itemMeasurement.weight = JSON.parse(message.body);
          this.scannedItem.itemMeasurement = ItemsHelper
            .convertMeasurementsToConfiguredUnits(this.scannedItem.itemMeasurement, this.dimensionUnit, this.weightUnit, this.volumeUnit);
          this.updateVolume();
        }
      );

      this.itemScrapeScreenSub = this.$store.state.stompClient.subscribe(STOMP_TOPICS.ITEM_SCRAPE_SCREEN, (message: Message) => {
        const messageData = JSON.parse(message.body);
        this.itemScrapeScreen = messageData.imageBase64;
        this.itemScrapeScreenLoading = !messageData.imageDownloaded;
      });

      this.itemWeighResultsSub = this.$store.state.stompClient.subscribe(
        STOMP_TOPICS.ITEM_WEIGH_RESULTS,
        (message: Message) => {
          const errorData = JSON.parse(message.body);
          errorData.isError ? UserActionResultSender.showErrorFeedback(errorData.responseCode) :
            UserActionResultSender.showSuccessFeedback(errorData.responseCode);
        });
      return;
    }
  }

  public async getScaleProperty(propertyId: string): Promise<any | undefined> {
    try {
      const message = await AXIOS.get(`/properties/${propertyId}`);
      return message.data.propValue;
    } catch (error) {
      UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      return undefined;
    }
  }

  public async getCapturedImage() {
    await this.testConnection();

    if (this.scannedItem.itemNumber && this.scannedItem.itemNumber !== "" && this.cameraConnected) {
      this.loading = true;
      AXIOS.post(`/item/capture/${this.scannedItem.itemNumber}`)
        .then((message) => {
          this.scannedItem.imageFilename = message.data.filePath;
          this.imageFilename = message.data.filePath;
          this.itemImage = message.data.base64Image;
        })
        .catch((error) => {
          UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
          this.itemImage = null;
          this.imageFilename = '';
        })
        .finally(() => {
          this.loading = false;
          this.updateTextColor();
        });
    }
  }

  public get2dImage() {
    this.itemScrapeScreenLoading = true;
    AXIOS.post(`/item/scan`)
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
        this.loading = false;
      });
  }

  public getItemImage() {
    if (this.scannedItem.imageFilename !== '') {
      this.loading = true;
      const params: { path?: string } = {};
      params.path = this.scannedItem.imageFilename;

      AXIOS.get(`/item/image`, {params})
        .then((message) => {
          this.imageFilename = message.data.filePath;
          this.itemImage = message.data.base64Image;
        })
        .catch((error) => {
          UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
          this.itemImage = null;
          this.imageFilename = '';
        })
        .finally(() => {
          this.loading = false;
          this.updateTextColor();
        });
    }
  }

  public isMandatory(userField: UserField) {
    if (userField.mandatory) {
      return this.isRequired;
    }
  }

  public beforeDestroy() {
    EventBus.$off(EVENTBUS_TOPICS.ITEM_MEASURE);

    if (this.itemMeasurementsSub !== undefined) {
      this.itemMeasurementsSub.unsubscribe();
    }

    EventBus.$off(EVENTBUS_TOPICS.ITEM_MEASURE_ERRORS);

    if (this.itemMeasurementsErrorsSub !== undefined) {
      this.itemMeasurementsErrorsSub.unsubscribe();
    }

    EventBus.$off(EVENTBUS_TOPICS.ITEM_WEIGH);

    if (this.itemWeighSub !== undefined) {
      this.itemWeighSub.unsubscribe();
    }

    EventBus.$off(EVENTBUS_TOPICS.ITEM_WEIGH_RESULTS);

    if (this.itemWeighResultsSub !== undefined) {
      this.itemWeighResultsSub.unsubscribe();
    }

    window.removeEventListener("keydown", this.handleGlobalKeydown);

    if (this.player) {
      this.player.dispose(); // Clean up Video.js player when the component is destroyed
    }
  }

  public async getItemFromBackend() {
    this.loading = true;
    const url = `/item/${this.scannedItem.itemNumber}`;
    const params: { secondaryField?: string | boolean } = {};

    if (this.scannedItem.secondaryField) {
      params.secondaryField = this.scannedItem.secondaryField;
    }
    AXIOS.get(url, {params})
      .then((message) => {
        if (message.data) {
          this.scannedItem = message.data;
          this.scannedItem.itemMeasurement = ItemsHelper.convertMeasurementsToConfiguredUnits(this.scannedItem.itemMeasurement, this.dimensionUnit, this.weightUnit, this.volumeUnit);
          this.existingItem = true;
        } else {
          this.existingItem = false;
        }
        this.getItemImage();
      })
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
        this.existingItem = false;
      })
      .finally(() => {
        this.loading = false;
        this.updateTextColor();
      });
  }

  public async getExistingItem() {
    await this.getCameraSettingsFromDatabase();
    if (this.captureAfterBarcodeScanProperty && this.cameraEnabled) {

      await this.getCapturedImage();
    }
    if (!this.secondaryFieldEnabled || (this.secondaryFieldEnabled && this.scannedItem.itemNumber !== "" && this.scannedItem.secondaryField !== "")) {
      await this.getItemFromBackend();
    }

    this.changeFocus(null);
  }

  public async handleScaleRequest() {
    const isScaleEnabled = await this.getScaleProperty("scaleEnabled");
    const scaleRequestMode = await this.getScaleProperty("scaleRequestMode");
    const shouldWeighBefore = await this.getScaleProperty(ScaleProperties.WEIGH_BEFORE);

    if (isScaleEnabled === "false" || shouldWeighBefore === "false" || scaleRequestMode === ScaleRequestMode.BROADCAST) {
      return;
    }

    AXIOS.post('/device/scale-weigh')
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      });
  }

  public updateTextColor() {
    if (this.itemHighlightingEnabled === ItemHighlightingMode.ENABLED) {
      if (this.existingItem) {
        this.textColorCss = 'text-input-red';
      } else {
        this.textColorCss = 'text-input-green';
      }
    }
  }

  public async getItemsFromBackend() {
    AXIOS.get("/item")
      .then((message) => {
        this.items = message.data.map((item: any) => {
          item.itemMeasurement = ItemsHelper.convertMeasurementsToConfiguredUnits(item.itemMeasurement, this.dimensionUnit, this.weightUnit, this.volumeUnit);
          const userFields: UserField[] = item.userFields;
          item.userFields = userFields.filter(value => value.fieldId.startsWith(USER_FIELDS.USER_FIELD_ID_PREFIX));
          return item;
        });
      })
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      });
  }

  public getProperties() {
    this.propertyService
      .getProperties()
      .then(value => this.properties = value)
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
          this.loading = false;
        }
      );
  }

  public async getDimensionUnitFromBackend() {
    this.loading = true;
    await Promise.all([
      this.propertyService.fetchAndSetProperty<string>(MeasureUnitKey.DIMENSION, (value) => {
        const unit = ItemsHelper.getDimensionUnitByValue(value);
        if (unit) {
          this.dimensionUnit = unit;
        } else {
          UserActionResultSender.showErrorFeedback('global.somethingWentWrong');
        }
      })
    ]);
  }

  public async getWeightUnitFromBackend() {
    await Promise.all([
      this.propertyService.fetchAndSetProperty<string>(MeasureUnitKey.WEIGHT, (value) => {
        const unit = ItemsHelper.getWeightUnitByValue(value);
        if (unit) {
          this.weightUnit = unit;
        } else {
          UserActionResultSender.showErrorFeedback('global.somethingWentWrong');
        }
      })
    ]);
  }

  public async getVolumeUnitFromBackend() {
    await Promise.all([
      this.propertyService.fetchAndSetProperty<string>(MeasureUnitKey.VOLUME, (value) => {
        const unit = ItemsHelper.getVolumeUnitByValue(value);
        if (unit) {
          this.volumeUnit = unit;
        } else {
          UserActionResultSender.showErrorFeedback('global.somethingWentWrong');
        }
      })
    ]);
  }

  public async getItemHighlightingFromBackend() {
    this.loading = true;
    await Promise.all([
      this.propertyService.fetchAndSetProperty<ItemHighlightingMode>(DimensioningModeKey.ITEM_HIGHLIGHTING, (value) => {
        this.itemHighlightingEnabled = value;
      }),
    ]);
  }

  public getUserFieldById(fieldId: string): UserField | undefined {
    return this.userFields.find(f => f.fieldId === fieldId);
  }

  public async getUserFieldsFromBackend() {
    AXIOS.get("/user-field")
      .then((message) => {
        this.userFields = message.data;
        this.enabledUserFields = this.userFields.filter(field => field.enabled && field.fieldId.startsWith(USER_FIELDS.USER_FIELD_ID_PREFIX));
        this.secondaryField = this.getUserFieldById(USER_FIELDS.SECONDARY_FIELD);
        this.setDefaultUserFieldsValues();
        if (this.secondaryField) {
          this.secondaryFieldEnabled = this.secondaryField.enabled;
        }
      }).catch((error) => {
      UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
    });
  }

  public saveItem() {
    this.loading = true;
    const apiPayload = {
      ...this.scannedItem,
      itemMeasurement: ItemsHelper.convertMeasurementsToDefaultUnits(this.scannedItem.itemMeasurement, this.dimensionUnit, this.weightUnit, this.volumeUnit)
    };
    const isNewItem = this.scannedItem.gendate === null || this.scannedItem.gendate === "";
    AXIOS.put("/item", apiPayload)
      .then((message) => {
        this.scannedItem = message.data;
        this.scannedItem.itemMeasurement = ItemsHelper.convertMeasurementsToConfiguredUnits(this.scannedItem.itemMeasurement, this.dimensionUnit, this.weightUnit, this.volumeUnit);
        this.getItemsFromBackend();
        isNewItem ?
          UserActionResultSender.showSuccessFeedback('items.success.itemCreated') :
          UserActionResultSender.showSuccessFeedback('items.success.itemUpdated');
        this.clear();
      })
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public initNewItem(): Item {
    return {
      itemNumber: "",
      secondaryField: "",
      imageFilename: "",
      scrapeScreenFileName: "",
      itemMeasurement: this.initNewMeasurements(),
      gendate: null,
      moddate: null,
      userFields: [],
    };
  }

  public initNewMeasurements(): ItemMeasurement {
    return {
      length: null,
      width: null,
      height: null,
      weight: null,
      volume: 0,
      dimensionUnit: "",
      weightUnit: "",
    };
  }

  public openDialog() {
    this.itemsDialog = true;
  }

  public closeDialog() {
    this.itemsDialog = false;
  }

  public async populateForm(item: Item) {
    this.scannedItem = {
      itemNumber: item.itemNumber,
      secondaryField: item.secondaryField,
      imageFilename: item.imageFilename,
      itemMeasurement: item.itemMeasurement,
      gendate: item.gendate,
      moddate: item.moddate,
      userFields: item.userFields
    };

    this.enabledUserFields.forEach(enabledUserField => {
      enabledUserField.currentValue = '';
      this.scannedItem.userFields.filter(field => field.fieldId === enabledUserField.fieldId)
        .forEach(field => enabledUserField.currentValue = field.currentValue);
    });
/*    if (this.secondaryFieldEnabled && this.secondaryField && this.secondaryField.fieldDataType === 'BOOLEAN') {
     this.scannedItem.secondaryField = item.secondaryField === "true";
    }*/
    this.itemsDialog = false;
    this.tab = 0;
    this.itemScrapeScreen = null;
    this.itemScrapeScreenLoading = false;
    await this.getItemFromBackend();
  }

  public setUserFieldValue(fieldId: string, value: string): void {
    const userFieldToUpdate = this.scannedItem.userFields.find(v => v.fieldId === fieldId);

    if (userFieldToUpdate) {
      // Update the current value if it's already in scannedItem.userFields
      userFieldToUpdate.currentValue = value;
    } else {
      // If not in scannedItem, look in the global userFields array
      const userField = this.userFields.find(v => v.fieldId === fieldId);

      if (userField) {
        // Clone the field to prevent unwanted mutations in userFields array
        const newUserField = {...userField, currentValue: value};

        // Push the new or modified user field into scannedItem.userFields
        this.scannedItem.userFields.push(newUserField);
      }
    }
  }

  public handleInput(fieldId: string, value: string): void {
    this.setUserFieldValue(fieldId, value);
  }

  public updateVolume() {
    const length = this.scannedItem.itemMeasurement.length !== null && this.scannedItem.itemMeasurement.length !== undefined
      ? this.scannedItem.itemMeasurement.length
      : 0;

    const width = this.scannedItem.itemMeasurement.width !== null && this.scannedItem.itemMeasurement.width !== undefined
      ? this.scannedItem.itemMeasurement.width
      : 0;

    const height = this.scannedItem.itemMeasurement.height !== null && this.scannedItem.itemMeasurement.height !== undefined
      ? this.scannedItem.itemMeasurement.height
      : 0;
    if (length > 0 && width > 0 && height > 0) {
      this.scannedItem.itemMeasurement.volume = ItemsHelper.roundToFixedDigits(this.getVolume(width * height * length), 7);
    } else {
      this.scannedItem.itemMeasurement.volume = 0;
    }
  }

  public getVolume(volumeInInitialUnit: number): number {
    let convertedVolume: number | null = 0;
    switch (this.dimensionUnit) {
      case DimensionUnit.INCHES:
        convertedVolume = ItemsHelper.convertVolume(volumeInInitialUnit, VolumeUnit.CUBIC_INCHES, this.volumeUnit);
        break;
      case DimensionUnit.CENTIMETERS:
        convertedVolume = ItemsHelper.convertVolume(volumeInInitialUnit, VolumeUnit.CUBIC_CM, this.volumeUnit);
        break;
      case DimensionUnit.MILLIMETERS:
        convertedVolume = ItemsHelper.convertVolume(volumeInInitialUnit, VolumeUnit.CUBIC_MM, this.volumeUnit);
        break;
    }

    if (convertedVolume !== null) {
      return convertedVolume;
    } else {
      return 0;
    }
  }

  public getItemImageFileName(): string {
    const images = require.context('@/assets/items-image', false, /\.(png|jpe?g|svg)$/);

    if (this.scannedItem.imageFilename) {
      const fileName = this.scannedItem.imageFilename;
      // Check if the file exists in the context
      if (images.keys().includes(`./${fileName}`)) {
        return images(`./${fileName}`);
      }
    }
    // Return default image if not found
    return images('./image_not_available.png');
  }

  public clear() {
    // Reset the scannedItem to a new blank item
    this.scannedItem = this.initNewItem();
    if (this.secondaryFieldEnabled && this.secondaryField) {
      this.scannedItem.secondaryField = this.secondaryField.defaultValue;
    }
    this.setDefaultUserFieldsValues();
    this.textColorCss = 'text-input-white';

    this.resetValidation();
    this.focusItemNumber();
    this.itemImage = null;
    this.imageFilename = '';
    this.itemScrapeScreen = null;
  }

  public isMeasurementsDisabled(): boolean {
    return !!(
      !this.scannedItem.itemNumber ||
      (this.secondaryFieldEnabled &&
        this.secondaryField &&
        this.secondaryField.fieldDataType !== 'BOOLEAN' &&
        !this.scannedItem.secondaryField)
    );
  }


  public deleteItems() {
    const selectedForDelete: ItemRequest[] = [];
    this.selectedItems.forEach(value => selectedForDelete.push({
      itemNumber: value.itemNumber,
      secondaryField: value.secondaryField
    }));

    AXIOS.delete("/item/delete", {data: selectedForDelete})
      .then(() => {
        this.getItemsFromBackend();
        UserActionResultSender.showSuccessFeedback('items.success.itemsDeleted');
      })
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
        this.closeDeleteItemDialog();
        this.selectedItems = [];
      });
  }

  public openDeleteItemDialog() {
    this.deleteItemDialogOpened = true;
  }

  public closeDeleteItemDialog() {
    this.deleteItemDialogOpened = false;
  }

  public changeFocus(index: number | null) {
    const currentRef = this.getCurrentRef();
    let currentRefNum = currentRef ? parseInt(currentRef) : 0;
    if (currentRefNum === 1 && !this.secondaryFieldEnabled) {
      currentRefNum += 1; // In case secondaryField is disabled, skip
    }

    // Determine the next reference element based on current and passed index
    const nextRefIndex = currentRefNum < 7 ? currentRefNum + 1 : index !== null ? index + 7 : null;
    const refElement = nextRefIndex !== null ? this.$refs[String(nextRefIndex)] : undefined;

    // Focus on the determined ref element
    if (refElement) {
      const elementToFocus = (Array.isArray(refElement) ? refElement[0] : refElement) as HTMLElement | undefined;
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus();
      }
    }
  }

  public getCurrentRef() {
    const activeElement = document.activeElement; // Get the currently focused element
    // Check if the active element matches any of your refs
    for (const key in this.$refs) {
      if (activeElement != null) {
        const refElement = this.$refs[String(key)] as HTMLElement | undefined; // Assert the type of $refs
        if (Array.isArray(refElement) && refElement[0].id === activeElement.id) {
          return key;
        } else if (refElement && refElement.id === activeElement.id) {
          return key; // Return the ref name if found
        }
      }
    }
    return null; // Return null if no match
  }

  public resetValidation() {
    const form = this.$refs.itemScan as Vue & { resetValidation: () => void };
    if (form && typeof form.resetValidation === 'function') {
      form.resetValidation();
    }
  }

  public isSaveDisabled(): boolean {
    return !this.scannedItem.itemNumber ||  // Item number is required
      !this.scannedItem.itemMeasurement.length || // Length is required
      !this.scannedItem.itemMeasurement.width ||  // Width is required
      !this.scannedItem.itemMeasurement.height || // Height is required
      !this.scannedItem.itemMeasurement.weight || // Weight is required
      (this.secondaryFieldEnabled && this.secondaryField && this.secondaryField.fieldDataType !== 'BOOLEAN' && !this.scannedItem.secondaryField) || // Conditionally check secondary field
      !this.mandatoryUserFieldsCompleted(); // Conditionally check user fields
  }

  public getFieldValue(item: Item, fieldId: string) {
    const userField = item.userFields.find(field => field.fieldId === fieldId);
    return userField ? userField.currentValue : ""; // Return empty if not found
  }

  public focusItemNumber() {
    this.$nextTick(() => {
      const itemNumberField = this.$refs[1];
      (itemNumberField as Vue & { resetValidation: () => boolean }).resetValidation();
      if (itemNumberField) {
        const elementToFocus = (Array.isArray(itemNumberField) ? itemNumberField[0] : itemNumberField) as HTMLElement | undefined;
        if (elementToFocus && typeof elementToFocus.focus === 'function') {
          elementToFocus.focus();
        }
      }
    });
  }

  public handleGlobalKeydown(event: KeyboardEvent) {
    if (document.activeElement && document.activeElement.tagName !== 'INPUT' && event.key !== null) {
      this.scannedItem.itemNumber = '';
      this.focusItemNumber();
    }
  }

  public async handleItemNumberEnterKeyDown() {
    if (!this.scannedItem.itemNumber) {
      return;
    }
    await this.getProductCodeFromBarcode();

    await this.getExistingItem();
    await this.handleScaleRequest();
  }

  public async getCameraSettingsFromDatabase() {
    await Promise.all([
      this.propertyService.fetchAndSetProperty<boolean>(CameraKey.CAMERA_ENABLED, (value) => {
        this.cameraEnabled = value;
      }),
      this.propertyService.fetchAndSetProperty<string>(CameraKey.CAMERA_IP_ADRESS, (value) => {
        this.cameraIpAddress = value;
      }),
      this.propertyService.fetchAndSetProperty<string>(CameraKey.CAMERA_IP_PORT, (value) => {
        this.cameraIpPort = value;
      }),
      this.propertyService.fetchAndSetProperty<boolean>(CameraKey.CAMERA_AUTO_AFTER_BARCODE_SCAN, (value) => {
        this.captureAfterBarcodeScanProperty = value;
      }),
      this.propertyService.fetchAndSetProperty<boolean>(CameraKey.CAMERA_AUTO_AFTER_MEASUREMENT, (value) => {
        this.captureAfterMeasurementProperty = value;
      }),
      this.propertyService.fetchAndSetProperty<boolean>(CameraKey.IMAGE_2D_ENABLED, (value) => {
        this.image2dEnabled = value;
      }),
    ]);
  }

  public async manualCaptureImage() {
    if (this.cameraEnabled) {
      await this.getCapturedImage();
    }
  }


  public testConnection(): Promise<void> {
    this.loading = true;
    return AXIOS.get(`/device/test-camera-connection`, {params: {address: this.getCameraAddressAndPort()}})
      .then((message) => {
        if (message.data) {
          this.cameraConnected = true;
          return;
        }
        this.cameraConnected = false;
        UserActionResultSender.showErrorFeedback('camera.error.notConnected');
      })
      .catch(error => {
        this.cameraConnected = false;
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getCameraAddressAndPort() {
    return this.cameraIpAddress.concat(":").concat(this.cameraIpPort);
  }

/*  public async getLiveCamera() {
    if (this.cameraConnected) {
      this.mjpegStreamUrl = '/frontend-api/item/camera/live';
      this.showCameraLiveModal = true;
    }
  }*/
  public async getLiveCamera() {
    if (!this.cameraConnected) { return; }

    this.retryCount = 0;
    this.streamError = false;
    this.tryStartStream();
  }


  public closeCameraLiveModal() {
    this.showCameraLiveModal = false;
    this.mjpegStreamUrl = '';
    this.retryCount = 0;
    this.streamError = false;
  }

  public async getProductCodeFromBarcode() {
    AXIOS.post(`/item/decode-item-barcode?itemBarcode=${this.scannedItem.itemNumber}`)
      .then((message) => {
        this.scannedItem.itemNumber = message.data;

      })
      .catch((error) => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }
  private tryStartStream() {
    this.mjpegStreamUrl = `/frontend-api/item/camera/live?ts=${Date.now()}`;
    this.showCameraLiveModal = true;

    setTimeout(() => {
      const img = new Image();
      img.src = this.mjpegStreamUrl;

      img.onload = () => {
        this.streamError = false;
      };

      img.onerror = () => {
        this.retryCount++;
        if (this.retryCount < this.maxRetries) {
          console.warn(`Retrying stream... attempt ${this.retryCount}`);
          this.tryStartStream();
        } else {
          console.error('Stream failed after max retries.');
          this.streamError = true;
          this.showCameraLiveModal = false;
        }
      };
    }, 500);
  }

  private created() {
    window.addEventListener("keydown", this.saveListener);
    window.addEventListener("keydown", this.clearListener);
  }

  private destroyed() {
    window.removeEventListener("keydown", this.saveListener);
    window.removeEventListener("keydown", this.clearListener);
  }

  private saveListener(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === "F2" && !this.isSaveDisabled()) {
      this.saveItem();
    }
  }

  private clearListener(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === "F8") {
      this.clear();
    }
  }

  private setDefaultUserFieldsValues() {
    if (this.secondaryField) {
      this.secondaryField.currentValue = this.secondaryField.defaultValue;
      this.scannedItem.secondaryField = this.secondaryField.currentValue;
    }
    this.enabledUserFields.forEach(userField => {
      if (userField.defaultValue != null) {
        userField.currentValue = userField.defaultValue;
        this.scannedItem.userFields.push(userField);
      } else {
        userField.currentValue = "";
      }
    });
  }

  private mandatoryUserFieldsCompleted() {
    let isCompleted = true;
    this.enabledUserFields.forEach(userField => {
      const itemUserField = this.scannedItem.userFields.find(itemField => itemField.fieldId === userField.fieldId);
      if (itemUserField) {
        if (itemUserField.mandatory && (!itemUserField.currentValue || itemUserField.currentValue === "")) {
          isCompleted = false;
        }
      } else if (userField.mandatory) {
        isCompleted = false;
      }
    });
    return isCompleted;
  }
}
